import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, Bot, Send, Search, Package, AlertTriangle, 
  FileText, Wrench, ShoppingCart, Loader2, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ShopAssistantPage() {
  const [activeTab, setActiveTab] = useState('chat');
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [lowStock, setLowStock] = useState(null);
  const [serviceType, setServiceType] = useState('');
  const [serviceParts, setServiceParts] = useState(null);
  const [poItems, setPoItems] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    // Add welcome message
    if (chatHistory.length === 0) {
      setChatHistory([{
        role: 'assistant',
        content: `👋 Hi! I'm your AI Shop Assistant. I can help you with:

• **Parts Inventory** - "Do we have oil filters in stock?"
• **Service Parts** - "What parts do I need for a 400hr service on unit 224?"
• **Stock Levels** - "What parts are running low?"
• **Purchase Orders** - Help you create PO drafts

How can I help you today?`
      }]);
    }
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  const sendMessage = async () => {
    if (!message.trim() || isLoading) return;

    const userMessage = message.trim();
    setMessage('');
    
    // Add user message to history
    setChatHistory(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/shop-assistant/chat`, {
        message: userMessage,
        conversation_history: chatHistory.slice(-10)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setChatHistory(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (err) {
      console.error('Chat error:', err);
      setChatHistory(prev => [...prev, { 
        role: 'assistant', 
        content: '❌ Sorry, I encountered an error. Please try again.' 
      }]);
      toast.error('Failed to get response');
    } finally {
      setIsLoading(false);
    }
  };

  const searchParts = async () => {
    if (!searchQuery.trim()) return;

    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/shop-assistant/parts/search?q=${encodeURIComponent(searchQuery)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(res.data);
    } catch (err) {
      toast.error('Search failed');
    }
  };

  const fetchLowStock = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/shop-assistant/low-stock?threshold=2`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLowStock(res.data);
    } catch (err) {
      toast.error('Failed to fetch low stock items');
    }
  };

  const fetchServiceParts = async (type) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/shop-assistant/service-parts/${type}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServiceParts(res.data);
      setServiceType(type);
    } catch (err) {
      toast.error('Failed to fetch service parts');
    }
  };

  const addToPO = (item) => {
    const existing = poItems.find(p => p.id === item.id);
    if (existing) {
      setPoItems(poItems.map(p => 
        p.id === item.id ? { ...p, quantity: p.quantity + 1 } : p
      ));
    } else {
      setPoItems([...poItems, { ...item, quantity: 1 }]);
    }
    toast.success(`Added ${item.description} to PO`);
  };

  const removeFromPO = (id) => {
    setPoItems(poItems.filter(p => p.id !== id));
  };

  const generatePO = async () => {
    if (poItems.length === 0) {
      toast.error('No items in purchase order');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/shop-assistant/generate-po`, {
        items: poItems.map(item => ({
          description: item.description,
          part_number: item.number,
          quantity: item.quantity,
          cost: item.cost
        }))
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Show PO preview
      toast.success('Purchase order generated!');
      console.log('PO Draft:', res.data);
      
      // You could open a modal here to show the PO
      alert(`Purchase Order Generated!\n\nSubtotal: $${res.data.subtotal.toFixed(2)}\nGST (5%): $${res.data.tax_amount.toFixed(2)}\nTotal: $${res.data.total.toFixed(2)}\n\nItems: ${res.data.items.length}`);
    } catch (err) {
      toast.error('Failed to generate PO');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };

  const refreshCache = async () => {
    setIsRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/shop-assistant/refresh-cache`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Inventory refreshed: ${res.data.total_parts} parts, ${res.data.parts_with_inventory} with stock quantities`);
      // Clear search results to force fresh data
      setSearchResults(null);
      setLowStock(null);
      setServiceParts(null);
    } catch (err) {
      toast.error('Failed to refresh inventory cache');
    } finally {
      setIsRefreshing(false);
    }
  };

  const serviceTypes = [
    { id: 'oil_change', name: 'Oil Change', icon: '🛢️' },
    { id: '400hr', name: '400hr Service', icon: '⏱️' },
    { id: 'brake', name: 'Brake Service', icon: '🛑' },
    { id: 'transmission', name: 'Transmission', icon: '⚙️' },
    { id: 'cooling', name: 'Cooling System', icon: '❄️' },
    { id: 'electrical', name: 'Electrical', icon: '⚡' }
  ];

  return (
    <div className="space-y-6" data-testid="shop-assistant-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-foreground flex items-center gap-2">
            <Bot className="h-5 w-5 text-purple-500" />
            AI Shop Assistant
          </h1>
          <p className="text-xs text-muted-foreground">Powered by local Wrench Board data</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={refreshCache} 
            disabled={isRefreshing}
            data-testid="refresh-inventory-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh Inventory'}
          </Button>
          {poItems.length > 0 && (
            <Button onClick={generatePO} className="bg-green-600 hover:bg-green-700">
              <ShoppingCart className="h-4 w-4 mr-2" />
              Generate PO ({poItems.length} items)
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="chat">
            <Bot className="h-4 w-4 mr-2" />
            Chat
          </TabsTrigger>
          <TabsTrigger value="search">
            <Search className="h-4 w-4 mr-2" />
            Search Parts
          </TabsTrigger>
          <TabsTrigger value="services">
            <Wrench className="h-4 w-4 mr-2" />
            Service Parts
          </TabsTrigger>
          <TabsTrigger value="low-stock" onClick={fetchLowStock}>
            <AlertTriangle className="h-4 w-4 mr-2" />
            Low Stock
          </TabsTrigger>
          {poItems.length > 0 && (
            <TabsTrigger value="po">
              <FileText className="h-4 w-4 mr-2" />
              PO Draft ({poItems.length})
            </TabsTrigger>
          )}
        </TabsList>

        {/* Chat Tab */}
        <TabsContent value="chat">
          <Card className="h-[600px] flex flex-col">
            <CardContent className="flex-1 flex flex-col p-4">
              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto space-y-4 mb-4">
                {chatHistory.map((msg, idx) => (
                  <div 
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] p-3 rounded-lg ${
                      msg.role === 'user' 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-secondary'
                    }`}>
                      <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-secondary p-3 rounded-lg">
                      <Loader2 className="h-5 w-5 animate-spin" />
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Input */}
              <div className="flex gap-2">
                <Input
                  placeholder="Ask about parts, inventory, or services..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                  disabled={isLoading}
                  className="flex-1"
                />
                <Button onClick={sendMessage} disabled={isLoading || !message.trim()}>
                  <Send className="h-4 w-4" />
                </Button>
              </div>

              {/* Quick Actions */}
              <div className="flex gap-2 mt-3 flex-wrap">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setMessage("What parts are running low on stock?")}
                >
                  Low stock items
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setMessage("What parts do I need for a 400hr service?")}
                >
                  400hr service parts
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setMessage("Do we have any oil filters in stock?")}
                >
                  Oil filters stock
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Search Tab */}
        <TabsContent value="search">
          <Card>
            <CardHeader>
              <CardTitle>Search Parts Inventory</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <Input
                  placeholder="Search by part name or number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchParts()}
                  className="flex-1"
                />
                <Button onClick={searchParts}>
                  <Search className="h-4 w-4 mr-2" />
                  Search
                </Button>
              </div>

              {searchResults && (
                <div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Found {searchResults.count} results for "{searchResults.query}"
                  </p>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {searchResults.results.map((part) => (
                      <div 
                        key={part.id}
                        className="flex justify-between items-center p-3 rounded-lg bg-secondary/30"
                      >
                        <div className="flex-1">
                          <p className="font-medium">{part.description}</p>
                          <p className="text-xs text-muted-foreground">
                            {part.number} • {part.category} • Qty: {part.quantity_on_hand}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-green-400 font-bold">{formatCurrency(part.cost)}</span>
                          <Button size="sm" variant="outline" onClick={() => addToPO(part)}>
                            Add to PO
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Service Parts Tab */}
        <TabsContent value="services">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Select Service Type</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  {serviceTypes.map((service) => (
                    <Button
                      key={service.id}
                      variant={serviceType === service.id ? 'default' : 'outline'}
                      className="h-20 flex flex-col gap-1"
                      onClick={() => fetchServiceParts(service.id)}
                    >
                      <span className="text-2xl">{service.icon}</span>
                      <span className="text-xs">{service.name}</span>
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {serviceParts && (
              <Card>
                <CardHeader>
                  <CardTitle>{serviceParts.service_type}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <p className="text-sm text-muted-foreground">Typical parts needed:</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {serviceParts.typical_parts_needed.map((part, idx) => (
                        <span key={idx} className="px-2 py-1 bg-secondary rounded text-xs">
                          {part}
                        </span>
                      ))}
                    </div>
                  </div>

                  <p className="text-sm font-medium mb-2">
                    Matching Inventory ({serviceParts.count} items):
                  </p>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {serviceParts.matching_inventory.slice(0, 15).map((part) => (
                      <div 
                        key={part.id}
                        className="flex justify-between items-center p-2 rounded bg-secondary/30"
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium truncate">{part.description}</p>
                          <p className="text-xs text-muted-foreground">
                            Qty: {part.quantity_on_hand}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-green-400 text-sm">{formatCurrency(part.cost)}</span>
                          <Button size="sm" variant="ghost" onClick={() => addToPO(part)}>
                            +
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Low Stock Tab */}
        <TabsContent value="low-stock">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-yellow-400">
                <AlertTriangle className="h-5 w-5" />
                Low Stock Items (≤2 units)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {lowStock ? (
                <>
                  <div className="mb-4 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                    <p className="text-sm">
                      <strong>{lowStock.count}</strong> items at or below threshold • 
                      Total value at risk: <strong>{formatCurrency(lowStock.total_value_at_risk)}</strong>
                    </p>
                  </div>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {lowStock.items.map((item) => (
                      <div 
                        key={item.id}
                        className="flex justify-between items-center p-3 rounded-lg bg-secondary/30"
                      >
                        <div className="flex-1">
                          <p className="font-medium">{item.description}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.number} • {item.category}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className={`font-bold ${item.quantity_on_hand === 0 ? 'text-red-400' : 'text-yellow-400'}`}>
                              {item.quantity_on_hand} in stock
                            </p>
                            <p className="text-xs text-muted-foreground">{formatCurrency(item.cost)} ea</p>
                          </div>
                          <Button size="sm" variant="outline" onClick={() => addToPO(item)}>
                            Reorder
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                  <p className="text-muted-foreground">Loading low stock items...</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PO Draft Tab */}
        <TabsContent value="po">
          <Card>
            <CardHeader>
              <CardTitle>Purchase Order Draft</CardTitle>
            </CardHeader>
            <CardContent>
              {poItems.length > 0 ? (
                <>
                  <div className="space-y-2 mb-4">
                    {poItems.map((item) => (
                      <div 
                        key={item.id}
                        className="flex justify-between items-center p-3 rounded-lg bg-secondary/30"
                      >
                        <div className="flex-1">
                          <p className="font-medium">{item.description}</p>
                          <p className="text-xs text-muted-foreground">{item.number}</p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => setPoItems(poItems.map(p => 
                                p.id === item.id && p.quantity > 1 ? { ...p, quantity: p.quantity - 1 } : p
                              ))}
                            >
                              -
                            </Button>
                            <span className="w-8 text-center">{item.quantity}</span>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => setPoItems(poItems.map(p => 
                                p.id === item.id ? { ...p, quantity: p.quantity + 1 } : p
                              ))}
                            >
                              +
                            </Button>
                          </div>
                          <span className="text-green-400 font-bold min-w-[80px] text-right">
                            {formatCurrency(item.cost * item.quantity)}
                          </span>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-400"
                            onClick={() => removeFromPO(item.id)}
                          >
                            ✕
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-border pt-4 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="font-medium">
                        {formatCurrency(poItems.reduce((sum, item) => sum + (item.cost * item.quantity), 0))}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">GST (5%)</span>
                      <span className="font-medium">
                        {formatCurrency(poItems.reduce((sum, item) => sum + (item.cost * item.quantity), 0) * 0.05)}
                      </span>
                    </div>
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total</span>
                      <span className="text-green-400">
                        {formatCurrency(poItems.reduce((sum, item) => sum + (item.cost * item.quantity), 0) * 1.05)}
                      </span>
                    </div>
                  </div>

                  <Button className="w-full mt-4 bg-green-600 hover:bg-green-700" onClick={generatePO}>
                    <FileText className="h-4 w-4 mr-2" />
                    Generate Purchase Order
                  </Button>
                </>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  No items added to purchase order yet. Use "Add to PO" from search or low stock items.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
